import { Timestamp } from 'firebase/firestore';

export const MOCK_POSTS = [
    {
        id: "trt-feature-1",
        slug: "trt-afrika-feature",
        title: "Faraja Media Visuals Featured by TRT Afrika: Documenting Luo Traditions",
        seoTitle: "Faraja Media Visuals Featured by TRT Afrika — Documenting Luo Traditions",
        excerpt: "Faraja Media's original photography was recently utilized by international broadcaster TRT Afrika to illustrate a feature on Kenyan cultural practices.",
        metaDescription: "Faraja Media's original photography was recently utilized by international broadcaster TRT Afrika to illustrate a feature on Kenyan cultural practices.",
        category: "culture",
        publishDate: { toDate: () => new Date("2026-04-19"), toMillis: () => new Date("2026-04-19").getTime() },
        readTime: "3 min read",
        image: "/images/optimized/photography-stories-coverage.webp",
        featured: true,
        canonicalUrl: "https://farajamedia.com/blog/trt-afrika-feature",
        tags: ["TRT Afrika", "Luo Traditions", "Faraja Media", "Cultural Documentation"],
        guestName: "Faraja Media Editorial Team",
        mentions: [{ name: "TRT Afrika", url: "https://www.instagram.com/trtafrika/" }],
        content: `Faraja Media is proud to have our visual storytelling recognized by international media partners.

In April 2026, TRT Afrika, the Turkish national broadcaster’s Africa service, highlighted a story on the cultural practice of professional mourners in the Luo community. The visual narrative accompanying this report originated from the Faraja Media archive.

### Documenting Authentic Narratives

We are committed to preserving and amplifying authentic Kenyan narratives. Our team spent months documenting the intricate details of funeral processions and the evolving economic landscape of traditional mourning.

Being featured on a platform with over 230,000 followers allows us to share these important cultural markers with a global audience, ensuring that Kenyan traditions are seen with the dignity and context they deserve.

View the original, uncut photo essay below.

![Luo funeral traditions as referenced by TRT Afrika](/images/optimized/photography-stories-coverage-3.webp)
*Original photo by Faraja Media depicting Luo funeral traditions.*

[Watch the full feature on TRT Afrika's Instagram Reel](https://www.instagram.com/reel/DXTP6dyAY2I/)
`
    },
    {
        id: "luo-traditions-deep-dive",
        slug: "luo-funeral-traditions-kenya",
        title: "Inside the Luo Funeral Rite: The History and Modern Economics of Professional Mourning",
        seoTitle: "Luo Funeral Traditions & Professional Mourners in Kenya — Faraja Media",
        excerpt: "An in-depth look at the cultural significance of grief in Luo tradition and the rise of the 'Wakili' — professional mourners.",
        metaDescription: "Deep dive into Luo funeral traditions, professional mourners (Wakili) in Western Kenya, and the intersection of culture and modern economics.",
        category: "culture",
        publishDate: { toDate: () => new Date("2026-04-20"), toMillis: () => new Date("2026-04-20").getTime() },
        readTime: "12 min read",
        image: "/images/optimized/respecting.webp",
        featured: false,
        canonicalUrl: "https://farajamedia.com/blog/luo-funeral-traditions-kenya",
        tags: ["Luo funeral traditions", "professional mourners Kenya", "Kenyan cultural practices", "Western Kenya funerals", "Wakili"],
        guestName: "Faraja Media Cultural Documentary Team",
        mentions: [{ name: "TRT Afrika", url: "https://www.instagram.com/trtafrika/" }],
        content: `The recent spotlight on Kenyan cultural practices by international media has brought a fascinating, often misunderstood tradition to the forefront: the role of professional mourners in Luo funeral rites.

### The Cultural Significance of Grief in Luo Tradition

In the Luo community of Western Kenya, a funeral is not just a farewell; it is a monumental communal event that reflects the stature of the deceased and the strength of their lineage. Grief is not a private affair—it is a public, shared expression of respect.

Traditionally, the "tero buru" (the ritual of driving cattle to the homestead during a funeral) and the intense wailing of relatives served to notify the spirits and the community that a great tree had fallen.

### The Rise of the 'Wakili' (Professional Mourner)

As the Kenyan society urbanizes and funeral traditions evolve, a new commercial element has emerged: the **Wakili**. These are men and women hired specifically to provide professional wailing, singing, and ritualistic mourning services.

While some cultural purists argue this commercializes grief, many families—especially those in the diaspora who cannot be physically present—view the Wakili as a vital bridge. They ensure the departed is honored with the traditional volume and intensity that their lineage demands.

### Economics of Cultural Preservation

The "mourning economy" in regions like Siaya and Kisumu is not just about grief; it is about employment and cultural continuity. Faraja Media’s documentary photography captures the grit and the grace of these individuals, who treat their role as a sacred service.

By documenting these rites, we aim to provide context to the global conversation initiated by outlets like TRT Afrika. We believe that whether grief is spontaneous or professional, its purpose remains the same: ensuring no one walks their final path in silence.

---
*Capture these moments for your family. Faraja Media provides cinematic funeral coverage across Kenya and seamless live streaming for families worldwide.*
`
    },
    {
        id: "regret-series-1",
        slug: "what-families-regret-most-photography",
        title: "The One Thing Families Regret Most About Funerals",
        seoTitle: "The One Thing Families Regret Most About Funerals — Faraja Media",
        excerpt: "Why relying on smartphones for funeral photography is the most common regret we hear from grieving families.",
        metaDescription: "Professional funeral photography vs smartphone photos. Why families regret missing professional coverage when documenting final farewells.",
        category: "memory-legacy",
        publishDate: { toDate: () => new Date("2026-04-19"), toMillis: () => new Date("2026-04-19").getTime() },
        readTime: "6 min read",
        image: "/images/optimized/photography-stories-coverage.webp",
        featured: true,
        canonicalUrl: "https://farajamedia.com/blog/what-families-regret-most-photography",
        tags: ["funeral photography", "grief", "memory", "regret", "kenya funeral"],
        guestName: "Faraja Media Documentary Team",
        content: `When the day finally arrives, it moves in a blur. Family members who haven't seen each other in years embrace. Tears are shed, eulogies are delivered, and hymns are sung. And then, abruptly, it is completely over.
    
It is a profound truth of grief that the very event designed to memorialize a loved one is almost impossible for the immediate family to clearly remember. The shock, the social exhaustion, and the emotional weight create a tunnel vision.

And this is where the regret happens.

Months later, when the dust has settled and the finality of the loss truly sets in, families go looking for the photographs. 

### "Uncle John took some on his phone..."

This is the sentence that precedes the deepest disappointment we hear. 

We live in an age where there are over 40 high-definition cameras sitting in the pockets of the congregation at any given funeral. Because of this, it is easy to assume that *someone* will capture the important moments. 

But a funeral is not a wedding. At a wedding, attendees are looking for joy, posing for shots, and deliberately taking pictures. At a funeral, taking your phone out feels intrusive to most. Those who do take photos are usually sitting in the back rows.

When families eventually compile the 'smartphone photos', they almost always discover the same things:
- 15 blurry photos of the back of people's heads.
- Dark, grainy shots of the altar.
- Unflattering, mid-blink photos of people crying.
- Absolutely zero photos of the beautiful, quiet moments of connection between siblings.
- No clear image of the floral tributes before they wilted.

### The True Value of a Professional

A trained documentary memorial photographer is not there just to use a professional camera. They are there to do the emotional labor of observation so that you don't have to.

When we document a service, we operate with a specific philosophy: **Invisible Presence**.

We know when to step back during a prayer. We know how to capture the strength in a mother's face without being intrusive. We know to photograph the handwritten notes on the wreaths. We know to capture the texture of the soil, the beauty of the choir, and the faces of the elders who traveled hundreds of kilometers to be there.

Our lenses allow us to capture tight, emotional portraits from 50 feet away, completely unnoticed by the grieving family. 

### The Regret of the Unrecorded Farewell

A funeral is an unrepeatable historical event for your family. It is the final chapter of a book. 

Choosing not to document it professionally is choosing to rely entirely on the fragile memory of people who are in shock. 

The greatest feedback we receive is not "these photos are beautiful." The greatest feedback we receive is: *"I didn't even notice this happen. I was so overwhelmed that day, but looking at these photos brings me a deep, lasting peace."*

Do not leave the final chapter of your loved one's legacy to a shaky smartphone in the back row. Document it with the dignity it deserves.
`
    },
    {
        id: "regret-series-2",
        slug: "my-brother-died-nairobi-livestreaming-saved-me",
        title: "My Brother Died in Nairobi. I Was in London. Here's What Saved Me.",
        seoTitle: "Coping with Grief from the Diaspora: A True Story — Faraja Media",
        excerpt: "A first-person account of navigating sudden loss from 7,000 kilometres away, and how professional livestreaming provided closure.",
        metaDescription: "A personal story of diaspora grief. How funeral livestreaming helps families abroad find closure when they cannot travel home.",
        category: "grief-comfort",
        publishDate: { toDate: () => new Date("2026-04-18"), toMillis: () => new Date("2026-04-18").getTime() },
        readTime: "8 min read",
        image: "/images/optimized/livestreaming-coverage.webp",
        featured: false,
        canonicalUrl: "https://farajamedia.com/blog/my-brother-died-nairobi-livestreaming-saved-me",
        tags: ["diaspora grief", "livestreaming", "funeral streaming", "london to nairobi"],
        guestName: "A Diaspora Client (Anonymous)",
        content: `*This is a true submission from a Faraja Media client based in the UK, shared with their permission to help other diaspora families understand the emotional impact of virtual presence.*

The phone call came at 3:00 AM on a Tuesday. It's the hour every diaspora Kenyan dreads. 

My older brother, David, had collapsed in Nairobi. By the time they reached the hospital, he was gone. He was 42.

The next 48 hours were a blur of shock, WhatsApp groups blowing up with hundreds of messages, and the frantic checking of flight prices. Because of a visa renewal process I was undergoing in the UK, stepping outside the country meant I would legally compromise my residency and my job. 

I couldn't go. 

The guilt of that realization is something I cannot fully describe. It felt like a betrayal. My brother was being laid to rest in our ancestral home in Murang'a, and I was going to be sitting in a gray apartment in London, 7,000 kilometres away.

### The "WhatsApp Video Call" Solution

Initially, my cousin offered to just "WhatsApp video call" me during the service. In the panic of the moment, I said yes, assuming that was my only option.

But two days before the burial, an elder in our church group here in London pulled me aside. She had lost her mother a year prior under similar circumstances. She looked me in the eye and said: *"Do not let them use a phone. You will hear the wind, the connection will drop when they get to the village, and you will spend the entire funeral screaming at the screen 'Hello? Can you hear me?' It will ruin your goodbye."*

She sent me the link to Faraja Media. I WhatsApp'd them at 11:00 PM UK time, desperate. They responded in ten minutes.

### The Day of the Funeral

On the day of the burial, I dressed in black. I made a cup of tea. I sat in my living room and I clicked the private YouTube link they had sent me.

What I saw broke me in the most necessary way. 

The image was crystal clear. It looked like a television broadcast. But more importantly, the audio was perfect. I could hear the choir singing the exact hymns David loved. I could hear the tremor in my aunt's voice during the eulogy. 

When the procession moved from the church down to the graveside — a walk of about a kilometre — the camera followed smoothly. The stream never cut out, not even once, despite Murang'a notoriously having terrible cellular reception. Faraja had brought their own satellite internet setup.

### What It Means to Be Present

Through that screen, I grieved with my family. I cried when they cried. I prayed when they prayed. I felt entirely connected to the earth of my home, despite the ocean between us.

When it was over, I closed the laptop. The silence in my London apartment was heavy, but the guilt was gone. I had been there. I hadn't missed the final goodbye. 

To anyone in the diaspora reading this, caught between the grief of loss and the physical reality of borders, visas, and finances: do not let distance steal your closure. Professional livestreaming isn't a luxury. When you cannot be there in the flesh, it is a psychological lifeline.`
    },
    {
        id: "regret-series-3",
        slug: "what-is-preserved-vs-what-is-lost-smartphone-video-fails",
        title: "Why Smartphone Videos Fail at Funerals",
        seoTitle: "Smartphone Video vs Professional Funeral Videography — Faraja Media",
        excerpt: "The technical and emotional reasons why trying to crowdsource funeral video coverage on phones leads to permanent loss of history.",
        metaDescription: "Why professional funeral videography captures what smartphones miss. Audio quality, stabilization, and emotional preservation.",
        category: "memory-legacy",
        publishDate: { toDate: () => new Date("2026-04-17"), toMillis: () => new Date("2026-04-17").getTime() },
        readTime: "5 min read",
        image: "/images/optimized/video-coverage.webp",
        featured: false,
        canonicalUrl: "https://farajamedia.com/blog/what-is-preserved-vs-what-is-lost-smartphone-video-fails",
        tags: ["videography", "memorial video", "funeral video audio", "legacy"],
        guestName: "Faraja Media Production Team",
        content: `A modern smartphone has a remarkable camera. It can shoot in 4K, stabilize movement reasonably well, and adjust to low light. 

So why do we continually hear from families who are devastated by the footage sent to them by relatives after a funeral?

The answer lies in understanding the immense difference between capturing a *visual* and preserving a *memory*. A funeral is an unstructured, highly dynamic, multi-sensory environment. When you rely on a guest's smartphone to document it, you are guaranteed to lose three critical things.

### 1. You Lose the Audio (The Most Important Part)

If you only remember one thing from this article, let it be this: **Audio is 70% of a memorial film.**

A smartphone's microphone is designed to pick up sound within a 3-foot radius of the person holding it. In a large church, or an open field, the phone will capture the rustling of clothes, the coughing of the person next to them, and the howling of the wind. 

The eulogy — the most important spoken words of the day — will be a thin, echoing, unintelligible mumble in the distance. 

At Faraja Media, we don't rely on our camera microphones. We bypass the room noise entirely. We plug dedicated, professional audio recorders directly into the PA/Sound system. When you watch our films, the eulogy sounds like an audiobook. Every word is preserved, perfectly isolated from the wind and the crowd.

### 2. You Lose the Attention of the Guest

When you ask a cousin or an uncle to "make sure you get a video of the burial," you are asking them to do a job. 

But they are grieving too. 

Inevitably, what happens is that the guest will film for three minutes, get tired, put their arm down, and forget they are recording. They will be overcome with emotion and have to walk away. They will receive a phone call in the middle of a crucial moment. 

A professional crew is emotionally detached from the loss, which means we are laser-focused on the technical execution. We do not get tired. We do not stop recording when it rains. We maintain framing, focus, and stability from the first minute to the last.

### 3. You Lose the Story

A smartphone video is a raw, disconnected clip. It's a file saved in a WhatsApp chat named \`VID-20260408-WA0012.mp4\`.

A professional memorial film is a narrative. We color-grade the footage to reflect the dignity of the day. We edit the key moments together. We layer the spoken eulogy over slow-motion footage of the family arriving. We add the favorite hymns of the deceased underneath the visuals. 

We don't just hand you raw clips; we create a cinematic archive. A documentary of a legacy that you can proudly show to your children, and they can show to theirs.

Don't let your family's history be lost to the wind and shaky hands. Preserve it properly.`
    },
    {
        id: "regret-series-4",
        slug: "burial-we-almost-didnt-film-drone-coverage",
        title: "The Burial We Almost Didn't Film — And Why Drone Coverage Matters",
        seoTitle: "The Power of Aerial Drone Coverage at Kenyan Funerals — Faraja Media",
        excerpt: "Why seeing the scale of the community from the sky completely changes how we process grief and memory.",
        metaDescription: "Why drone coverage is becoming essential for Kenyan funerals. Capturing the full scale of community, rural logistics, and the final resting place.",
        category: "behind-scenes",
        publishDate: { toDate: () => new Date("2026-04-16"), toMillis: () => new Date("2026-04-16").getTime() },
        readTime: "7 min read",
        image: "/images/optimized/drone-coverage.webp",
        featured: false,
        canonicalUrl: "https://farajamedia.com/blog/burial-we-almost-didnt-film-drone-coverage",
        tags: ["drone photography", "aerial coverage", "rural funeral", "kenyan community"],
        guestName: "Faraja Media Drone Pilot",
        content: `When a family is looking at our package options, Drone Aerial Coverage is often the one they hesitate on. 

*"Do we really need a drone at a funeral?"* they ask. *"It seems a bit cinematic. A bit too Hollywood."*

We understand the hesitation. For decades, funeral photography has been strictly grounded. But there was one specific service in 2023 that completely changed how families view this service.

### The Procession Over the Ridge

We were in a deep rural ward in Nyeri. The deceased was a beloved former headmaster who had taught three generations of the local community.

The family had booked our standard video and photography package. On the morning of the service, understanding the stature of the man, I decided to pack our DJI Mavic 3 drone in the truck anyway, entirely at my own discretion. 

The church service went beautifully, but it was the procession to the home that demonstrated the sheer scale of the event. The convoy of cars, buses, and walking mourners was immense. 

From the ground, it just looked like a lot of people crowding a dirt road. It was chaotic, dusty, and visually cramped. You couldn't see the beginning or the end of the line.

I quietly launched the drone.

### A New Perspective on Legacy

When I brought the drone up to 100 meters, the monitor on my controller showed a breathtaking image. 

The procession wasn't chaotic at all. It was a perfectly winding ribbon of hundreds of people, stretching for over two kilometers, snaking through the green, terraced tea farms of Nyeri. 

For the first time, you could visually comprehend the impact this one man had made. From the sky, you saw a community moving as a single, unified organism to honor him. 

When we delivered the final film to the family, we opened with that devastatingly beautiful aerial shot. The eldest son called us the next day. He was weeping. 

*"I had no idea,"* he said. *"I was in the front car looking straight ahead. I had no idea that many people walked with us. Seeing that from above... it made me realize how loved he was."*

### Why the Sky Matters

Ground cameras capture the *individuals*. Drones capture the *community*. 

In Kenyan culture, the journey back to the ancestral home is a sacred part of the rites. The topography of the land, the procession through the village, the scale of the gathering — these are vital components of the story.

A drone provides context that is physically impossible to see from the ground. It shows the vastness of the hills. It shows the sheer number of cars parked in the fields. It shows the physical placement of the final resting place within the beautiful, wider geography of home.

It is not about making the video look like a movie. It is about accurately documenting the scale of the respect commanded by your loved one.`
    },
    {
        id: "mock-1",
        slug: "kenya-death-registration-2026-complete-guide",
        title: "Kenya Death Registration 2026: Complete Step-by-Step Guide",
        seoTitle: "Kenya Death Registration 2026: Complete Step-by-Step Guide — Faraja Media",
        excerpt: "How to register a death in Kenya in 2026: Form D1, eCitizen death certificate, burial permit, NSSF Phase 4 funeral grant, SHIF notification, and diaspora coordination. Updated guide from Faraja Media.",
        metaDescription: "How to register a death in Kenya in 2026: Form D1, eCitizen death certificate, burial permit, NSSF Phase 4 funeral grant, SHIF notification, and diaspora coordination. Updated guide from Faraja Media.",
        category: "funeral-guidance",
        publishDate: { toDate: () => new Date("2026-04-01"), toMillis: () => new Date("2026-04-01").getTime() },
        readTime: "14 min",
        image: "/images/optimized/memorial.webp",
        featured: false,
        canonicalUrl: "https://farajamedia.com/blog/kenya-death-registration-2026-complete-guide",
        tags: ["death-registration-kenya", "death-certificate-ecitizen", "form-d1-kenya", "nssf-funeral-grant", "burial-permit-kenya", "diaspora-kenya-funeral", "shif-notification", "kenya-2026"],
        guestName: "Faraja Media Editorial Team",
        content: `Losing a loved one is one of the hardest things a family will ever face. What follows — the forms, the offices, the deadlines — should not add to that burden.
    
    At Faraja Media, we have walked alongside Kenyan families through hundreds of farewells. Here is the complete administrative guide for 2026.`
    }
];
